enum WorkflowState {
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  FAILED = 'failed',
  INITIATED = 'initiated',
  PENDING = 'pending',
  RUNNING = 'running',
}

enum RowStatus {
  CLASSIFYING = 'classifying',
  COMPLETED = 'completed',
  EXTRACTING = 'extracting',
  FAILED = 'failed',
  INGESTING = 'ingesting',
  PENDING = 'pending',
  PROCESSING = 'processing',
  RUNNING = 'running',
  VALIDATION_FAILED = 'validation_failed',
  WAITING_FOR_APPROVAL = 'waiting_for_approval',
  WAITING_FOR_REVIEW = 'waiting_for_review',
  WAITING_ON_CUSTOMER = 'waiting_on_customer',
  WORKFLOW_COMPLETED = 'workflow_completed',
}

enum WorkflowLogStatus {
  BROWSER_AGENT_COMPLETED = 'browser_agent_completed',
  BROWSER_AGENT_RUN_INITIATED = 'browser_agent_run_initiated',
  BROWSER_AGENT_INITIATED = 'browser_agent_initiated',
  BROWSER_AGENT_TASK_FAILED = 'browser_agent_task_failed',
  DOCUMENT_CLASSIFIED = 'document_classified',
  DOCUMENT_VALIDATION_FAILED = 'document_validation_failed',
  DOCUMENT_VALIDATION_SUCCEEDED = 'document_validation_succeeded',
  EXCEL_EXPORT_APPROVED = 'excel_export_approved',
  EXCEL_EXPORT_AUTO_APPROVED = 'excel_export_auto_approved',
  EXTRACTION_COMPLETED = 'extraction_completed',
  EXTRACTION_STARTED = 'extraction_started',
  FORM_FILLING_COMPLETED = 'form_filling_completed',
  WORKFLOW_RESUMED = 'workflow_resumed',
  WAITING_FOR_REVIEW = 'waiting_for_review',
  FORM_FILLING_FAILED = 'form_filling_failed',
  WAITING_FOR_APPROVAL = 'waiting_for_approval',
  PREPROCESSING_INITIATED = 'preprocessing_initiated',
  // E.g. due to an internal error
  WORKFLOW_COMPLETED = 'workflow_completed',
  WORKFLOW_INITIATED = 'processing_initiated',
  PREREQUISITE_VALIDATION_SUCCEEDED = 'prerequisite_validation_succeeded',
  FORM_FILLING_STARTED = 'form_filling_started',
  REVIEW_COMPLETED = 'review_completed',
  WORKFLOW_FAILED = 'workflow_failed',
  PRECHECKLIST_SUCCEEDED = 'prechecklist_succeeded',
  PRECHECKLIST_FAILED = 'prechecklist_failed',
  PREREQUISITE_VALIDATION_FAILED = 'prerequisite_validation_failed',
  PROCESSING_INITIATED = 'processing_initiated',
  WORKFLOW_CREATED = 'workflow_created',
  PREPROCESSING_COMPLETED = 'preprocessing_completed',
  WORKFLOW_CANCELLED = "workflow_cancelled"
}

enum JobState {
  COMPLETED = 'completed',
  FAILED = 'failed',
  PAUSED = 'paused',
  PENDING = 'pending',
  RUNNING = 'running',
}

enum FieldState {
  COMPLETED = 'completed',
  FAILED = 'failed',
  INVALIDATED = 'invalidated',
  RUNNING = 'running',
}

enum DocumentState {
  COMPLETED = 'completed',
  FAILED = 'failed',
  PENDING = 'pending',
  RUNNING = 'running',
}

type WorkflowLogRecord = {
  log_record_id: string;
  log_record_payload?: any;
  log_record_status: WorkflowLogStatus;
  log_record_timestamp: string;
};

type WorkflowLog = {
  workflow_log_id: string;
  workflow_log_records: WorkflowLogRecord[];
};

type WorkflowRunListObject = {
  workflow_context: WorkflowContext | null;
  workflow_log: WorkflowLog | null;
  workflow_run: WorkflowRun;
  workflow_run_log_statuses: string[];
};

type IngestionSource = {
  source_type: string;
  source_value: string;
};

type DocumentStore = {
  created_at: string;
  dependent_jobs: any;
  document_store_id: string;
  documents: any;
  ingestion_source: IngestionSource | null;
  updated_at: string;
};

type WorkflowJob = {
  created_at: string;
  dependencies: any[];
  job_args: Record<string, any>;
  job_id: string;
  job_name: string;
  job_type: string;
  queries: Record<string, any>;
  tags: string[];
  updated_at: string;
};

type WorkflowContext = {
  created_at: string;
  dependent_jobs: Record<string, any>;
  document_states: Record<
    string,
    {
      document_state: DocumentState;
    }
  >;
  job_states: Record<
    string,
    {
      job_state: JobState;
    }
  >;
  started_at: string;
  updated_at: string;
  workflow_context_id: string;
  workflow_errors: string | null;
  workflow_id: string;
  workflow_state: WorkflowState;
};

type WorkflowRun = {
  browser_run_ids: string[];
  created_at: string;
  document_store: DocumentStore;
  email: string;
  linear_ticket_url?: string | null;
  status: RowStatus;
  updated_at: string;
  version_tag: string;
  workflow_context: WorkflowContext;
  workflow_context_id: string;
  workflow_id: string;
  workflow_log: WorkflowLog | null;
  workflow_outcomes?: any | null;
  workflow_run_description: string;
  workflow_run_id: string;
  workflow_run_name: string;
};

type DocumentMetadata = {
  original_filename: string;
  page_count: number;
};

type DocumentEmailMetadata = {
  date: string;
  filename: string;
  message_id: string;
  page_count: string;
  sender: string;
  subject: string;
  to: string;
};

export type {
  WorkflowRun,
  WorkflowRunListObject,
  WorkflowContext,
  DocumentStore,
  IngestionSource,
  WorkflowJob,
  WorkflowLog,
  WorkflowLogRecord,
  DocumentMetadata,
  DocumentEmailMetadata,
};

export {
  WorkflowState,
  RowStatus,
  WorkflowLogStatus,
  JobState,
  FieldState,
  DocumentState,
};
